import { useEffect } from 'react'
import axios, { AxiosResponse } from 'axios'
import { useApp } from './useApp'
import { ErrorMessages, Response } from '../types/api'
import { DeviceHwID } from '../types/models'
import { PortNumber } from '../types/ports'
import { SystemControl } from '../classes/SystemControl'
import { sessionExpired, trySessionRefresh } from '../utils/auth'
import { Safety } from '../classes/Safety'

export const API_VERSION = 'v1'

export type AxiosErrorMessage = Record<string, string[]>

/**
 * Convert a resolved axios response into our Response format.
 *
 * Axios resolves - instead of rejecting - responses that never got an HTTP status:
 * `settle()` treats a falsy `response.status` as a success, and the browser reports
 * status 0 for a request that was aborted before it completed. The most common cause
 * here is the page navigating away mid-request (the version check in `Safety.checkVersion()`
 * assigns `document.location` while requests are in flight). The body of such a
 * response is an empty string, so passing it on as data hands the caller a payload of
 * the wrong shape - e.g. `setPrices('')`, which then crashes on `prices.some(...)`.
 * Report it as a failed request instead.
 */
function toResult(res: AxiosResponse | undefined): Response {
  if (!res || !res.status) {
    return {
      success: false,
      messages: {
        request: ['Request did not complete']
      }
    }
  }

  return {
    success: true,
    data: 'data' in res ? res.data : null
  }
}

export type AxiosProps = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url: string,
  disabled?: boolean,
  token?: string,
  data?: Record<string, unknown>
}

/**
 * Execute a call to remote URL and process the result to Response format.
 * @param props
 */
export async function callAxios<Type>(props: AxiosProps, data: Object | undefined = undefined): Promise<Response<Type>> {
  const { method, url } = props
  const headers: {
    Authorization?: string,
    'Content-Type': string
    } = {
      'Content-Type': 'application/json'
    }
  const token = Safety.get('token')
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  let result: Response = {
    success: false,
    messages: {
      request: ['No response received']
    }
  }

  await axios({ method: method || 'GET', url: `/api/${API_VERSION}${url.startsWith('/') ? '' : '/'}${url}`, headers, data })
    .then(res => {
      // Handle success case.
      result = toResult(res)
    })
    .catch(async err => {
      // Log out if session expired.
      if (err.response && err.response.status === 401 && url !== '/auth/login') {
        const token = await trySessionRefresh()
        if (token) {
          headers.Authorization = `Bearer ${token}`
          await axios({ method: method || 'GET', url: `/api/${API_VERSION}${url.startsWith('/') ? '' : '/'}${url}`, headers, data }).then(res => {
            result = toResult(res)

          }).catch(err => {
            console.error(err)
            result = {
              success: false,
              messages: {
                request: [err.response ? `${err.response.status} ${err.response.statusText || 'Unknown'}` : 'Unknown error']
              }
            }
          })

        } else {
          sessionExpired()
        }
        // Handle error case.
      } else if (err.response && err.response.data && err.response.data.errors) {
        result = {
          success: false,
          messages: err.response.data.errors
        }
      } else if (err.response && err.response.status) {
        result = {
          success: false,
          messages: {
            request: [`${err.response.status} ${err.response.statusText || 'Unknown'}`]
          }
        }
      } else {
        console.error(err)
        result = {
          success: false,
          messages: {
            request: ['Unknown error']
          }
        }
      }
    })

  return result
}

export type AxiosHookProps<Type> = AxiosProps & {
  onError?: (errors: ErrorMessages) => void,
  onSuccess?: (t: Type) => void
  observe?: [DeviceHwID, PortNumber][]
  reload?: boolean
}

/**
 * Helper hook to call API using axios.
 */
export function useAxios<Type>(props: AxiosHookProps<Type>) {
  const { url, data, onError, onSuccess, disabled, observe } = props

  const app = useApp()
  const messageCounts = (observe || []).map(([hwId, portNumber]) => SystemControl.count(hwId, portNumber))
  const messageCount = messageCounts.reduce((prev, cur) => prev + cur, 0)

  useEffect(() => {
    if (!disabled) {
      callAxios<Type>(props, data).then(resp => {
        if (resp.success) {
          onSuccess && onSuccess(resp.data)
        } else {
          if (onError) {
            onError(resp.messages)
          } else {
            app.addError(resp)
          }
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, data, disabled, messageCount])
}

export type AxiosesHookProps<Type1, Type2> = AxiosProps & {
  onError?: (errors: ErrorMessages) => void,
  onSuccess?: (t1: Type1, t2: Type2) => void
}

/**
 * Helper hook to call API twice but triggering onSuccess only when both calls are complete.
 */
export function useAxioses<Type1, Type2>(props1: AxiosHookProps<Type1>, props2: AxiosesHookProps<Type1, Type2>) {
  const { url, data, onError, disabled } = props1

  const app = useApp()

  useEffect(() => {
    if (!disabled) {
      callAxios<Type1>(props1, data).then(resp => {
        if (resp.success) {
          const { data, onError, onSuccess, disabled } = props2
          if (!disabled) {
            callAxios<Type2>(props2, data).then(resp2 => {
              if (resp2.success) {
                onSuccess && onSuccess(resp.data, resp2.data)
              } else {
                if (onError) {
                  onError(resp2.messages)
                } else {
                  app.addError(resp2)
                }
              }
            })
          }
        } else {
          if (onError) {
            onError(resp.messages)
          } else {
            app.addError(resp)
          }
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, data, disabled, props2.token, props2.url, props2.data, props2.disabled])
}
